import React, { useContext, useEffect, useRef, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { get, put, del } from 'helpers/api_helper'
import { store as socketStore } from 'components/Common/Socket/store'
import { newPlayerOperationId } from '../UserAccounts/PlayerAccounts/playerInvitationState'
import EditorNavigation from '../UserAccounts/EditorNavigation'
import { eventCommandMessage } from './runEventCommand'
import { eventWebPath } from './eventWebLink'
import { DeleteEventRule, DisableEventCopy, EnableEventCopy } from './eventStatusCopy'

const refusalText = code => eventCommandMessage(code, 'The action could not be completed. Refresh the event and try again.')

export default function EventActions({ event, user, onChanged, disabled }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const [confirm, setConfirm] = useState(null), [result, setResult] = useState({})
  const active = useRef(null), alive = useRef(true), changed = useRef(onChanged)
  changed.current = onChanged
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false; active.current?.cleanup(); active.current = null }
  }, [])
  const maintenance = event?.maintenance === true
  const finish = (request, success, message) => {
    if (active.current !== request || !alive.current) return
    request.cleanup(); active.current = null; setConfirm(null)
    setResult({ status: success ? 'saved' : 'error', message: message || (success ? 'Event status updated.' : 'The action could not be completed.') })
    if (success) changed.current?.(request.type)
  }
  const check = async request => {
    try {
      const response = await get('/admin/event-details/' + event._id)
      const current = response.data?.event
      const matched = request.type === 'deleted' ? current?.status === 'DELETED' : request.type === 'disabled' ? current?.status === 'DISABLED'
        : request.type === 'accepted' ? current?.status === 'ACCEPTED' : request.type === 'maintenance' ? current?.maintenance === true
          : current?.maintenance === false
      if (response.data?.id === event._id && matched) finish(request, true)
    } catch (error) { if (request.type === 'deleted' && error.response?.status === 404) finish(request, true) }
  }
  const run = async type => {
    if (active.current) return
    const request = { type, correlationId: newPlayerOperationId() }
    const receive = message => {
      if (message?.correlationId !== request.correlationId || message.eventId !== event._id) return
      if (typeof message.success === 'boolean') finish(request, message.success, message.success ? undefined : refusalText(message.message))
    }
    const timer = setTimeout(() => { if (active.current === request) setResult({ status: 'unknown' }) }, 60000)
    const polling = setInterval(() => check(request), 3000)
    request.cleanup = () => { clearTimeout(timer); clearInterval(polling); socket?.off('events-response', receive) }
    active.current = request; socket?.on('events-response', receive); setResult({ status: 'pending' })
    try {
      const config = { headers: { 'X-Correlation-Id': request.correlationId } }
      if (type === 'deleted') await del('/admin/events/' + event._id, { socketClientId }, config)
      else await put('/admin/events/' + event._id + '/status/' + type, { socketClientId }, config)
    } catch (error) {
      if (error.response?.status >= 400 && error.response.status < 500 && ![408, 429].includes(error.response.status)) finish(request, false, refusalText(error.response.data?.message))
      else if (active.current === request) setResult({ status: 'unknown' })
    }
  }
  const pending = ['pending', 'unknown'].includes(result.status)
  return <div className="event-header-actions">
    <EditorNavigation pending={pending} dirty={false} />
    <a className="btn btn-secondary" target="_blank" rel="noopener noreferrer" href={process.env.REACT_APP_WEB_URL + eventWebPath(event._id, event.name)}>View ↗</a>
    <button type="button" className="btn btn-warning" disabled={disabled || pending || !event.competitions?.length} onClick={() => run(maintenance ? 'resumeMaintenance' : 'maintenance')}>{maintenance ? 'Disable Maintenance' : 'Enable Maintenance'}</button>
    <button type="button" className="btn btn-primary" disabled={disabled || pending} onClick={() => setConfirm(event.status === 'DISABLED' ? 'accepted' : 'disabled')}>{event.status === 'DISABLED' ? 'Enable' : 'Disable'}</button>
    {user?.role === 'superAdmin' && <button type="button" className="btn btn-danger" disabled={disabled || pending} onClick={() => setConfirm('deleted')}>Delete Event</button>}
    {result.status === 'pending' && <p role="status">Waiting for status confirmation…</p>}
    {result.status === 'unknown' && <p role="status">Status is not confirmed. <button type="button" className="btn btn-link" onClick={() => check(active.current)}>Check status</button></p>}
    {result.message && <p role={result.status === 'error' ? 'alert' : 'status'} className={result.status === 'error' ? 'text-danger' : 'text-success'}>{result.message}</p>}
    <Modal isOpen={Boolean(confirm)} toggle={() => { if (!pending) setConfirm(null) }}><ModalHeader>{confirm === 'deleted' ? 'Delete event?' : confirm === 'accepted' ? 'Enable event?' : 'Disable event?'}</ModalHeader>
      <ModalBody>{confirm === 'deleted' ? <><p>The event and its information will no longer be available. Confirm deletion.</p><DeleteEventRule /></> : confirm === 'accepted' ? <EnableEventCopy name={event.name} /> : <DisableEventCopy name={event.name} />}</ModalBody>
      <ModalFooter><button type="button" className="btn btn-light" disabled={pending} onClick={() => setConfirm(null)}>Cancel</button><button type="button" className="btn btn-primary" disabled={pending} onClick={() => run(confirm)}>Confirm</button></ModalFooter>
    </Modal>
  </div>
}
